import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import type { ColDef } from 'ag-grid-community';
import FacilityOnboardingQueue from '../../features/abf/onboarding/FacilityOnboardingQueue';
import NewFacilityLanes from '../../features/abf/onboarding/NewFacilityLanes';
import {ForwardFlowCheckerBar} from '../../features/abf/forwardFlow/ForwardFlowCheckerBar';
import { InstitutionalDataGrid } from '../shared';
import { axiomTheme } from '../../styles/designTokens';
import { ABF_HOME_PATH, facilityWorkspacePath } from '../../features/abf/routes';
import { ViewportToast, type ActionFeedbackState } from '../../features/abf/components/ActionFeedback';
import {
  EmptyStateBox,
  MetricCard,
  MetricLabel,
  MetricStrip,
  MetricSubtext,
  MetricValue,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  RowSubtext,
  RowTitle,
  StatusPill,
  WorkspaceButton,
  WorkspacePanel,
  moneyCompact,
  moneyFull,
  numberCompact,
  percent,
} from './shared/CreditWorkspace';



const FacilityCell = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  min-width: 0;
  overflow: hidden;
  line-height: 1.2;
`;

const FacilityName = styled.div`
  overflow: hidden;
  color: ${axiomTheme.color.text.primary};
  font-size: 0.76rem;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FacilityDetails = styled.div`
  overflow: hidden;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: 0.38rem;
  text-overflow: ellipsis;
  white-space: nowrap;

  .code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.69rem;
  }
`;

const numeric = (value: any) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const centeredCell = {
  display: 'flex',
  alignItems: 'center',
} as const;

const rightCell = {
  ...centeredCell,
  justifyContent: 'flex-end',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontWeight: 750,
  fontVariantNumeric: 'tabular-nums',
} as const;

/**
 * The ABF facility list: commitment, drawn, borrowing base and availability tiles over a simple
 * grid of the official IBOR-backed facilities. Standalone it carries its own header and the
 * onboarding queue; embedded (the Facilities segment of the ABF home) it is the tiles and the
 * grid only, because the home supplies the header and the onboarding entry.
 */
const ABFManagementScreen: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  // Command bar "ABF <name>" opens this page with ?q= to pre-filter the worklist.
  const [searchParams] = useSearchParams();
  const worklistQuery = searchParams.get('q') || '';
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [details, setDetails] = useState<Record<number, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [choosingLane, setChoosingLane] = useState(false);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // The facility register is the list: the same records the monitoring board reads. The
      // rows used to be picked out of the IBOR position blotter by asset-class display name,
      // which emptied the list when that name was renamed ("ABF Facility" became "Asset-Based
      // Finance" in the classification tree).
      const res = await axios.get('/api/credit/abf/facilities');
      const facilities: any[] = Array.isArray(res.data) ? res.data : [];
      const hydrated = await Promise.all(facilities.map(async (facility: any) => {
        try {
          // Facility figures plus its current IBOR position, when it has one.
          const detail = await axios.get(`/api/credit/abf/facilities/${facility.facilityId}`);
          return [facility.facilityId, detail.data] as const;
        } catch {
          // Keep the row with the register's own fields; never manufacture facility metrics.
          return [facility.facilityId, null] as const;
        }
      }));
      const detailById = Object.fromEntries(hydrated);
      // A paid-off or termed-out facility with nothing drawn is not a holding; an ACTIVE
      // facility with nothing drawn (commitment only) is.
      const live = facilities
        .filter((f: any) => numeric(f.drawnAmount) !== 0 || !f.status || f.status === 'ACTIVE')
        .map((f: any) => ({ ...f, id: f.facilityId }));
      setRows(live);
      setDetails(detailById);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to load ABF facilities';
      setError(message);
      setFeedback({ tone: 'error', message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rowDetails = rows
    .map(row => details[row.id])
    .filter(Boolean);

  const totalCommitment = rowDetails.reduce((sum, d) => sum + numeric(d.totalCommitment), 0);
  const totalDrawn = rowDetails.reduce((sum, d) => sum + numeric(d.drawnAmount), 0);
  const totalBorrowingBase = rowDetails.reduce((sum, d) => sum + numeric(d.borrowingBase), 0);
  const totalAvailability = rowDetails.reduce((sum, d) => sum + numeric(d.excessAvailability), 0);
  const passCount = rowDetails.filter(d => d.advanceRateTestStatus === 'PASS').length;
  const failCount = rowDetails.filter(d => d.advanceRateTestStatus && d.advanceRateTestStatus !== 'PASS').length;
  const avgUtilization = totalCommitment > 0 ? (totalDrawn / totalCommitment) * 100 : 0;

  const openFacilityWorkspace = useCallback((row: any) => {
    const facilityId = row?.facilityId ?? row?.detail?.facilityId;
    if (!facilityId) {
      const message = 'Facility identity is not available for this row. Refresh the ABF worklist and try again.';
      setError(message);
      setFeedback({ tone: 'error', message });
      return;
    }
    navigate(facilityWorkspacePath(facilityId));
  }, [navigate]);

  const facilityRows = useMemo(() => rows.map(row => {
    const detail = details[row.id] || {};
    return {
      ...row,
      detail,
      positionId: detail.positionId ?? null,
      facilityName: detail.facilityName || row.facilityName,
      facilityCode: detail.facilityCode || row.facilityCode || row.cusip || 'ABF facility',
      obligorName: detail.obligorName || row.obligorName || '--',
      collateralType: detail.collateralType || row.collateralType || '--',
      currency: detail.currency || 'USD',
      drawnAmount: numeric(detail.drawnAmount ?? row.drawnAmount),
      borrowingBase: numeric(detail.borrowingBase),
      excessAvailability: numeric(detail.excessAvailability),
      delinquencyPct: numeric(detail.delinquencyPct),
      netLossRatePct: numeric(detail.netLossRatePct),
      numCollateralAssets: numeric(detail.numCollateralAssets),
      advanceRateTestStatus: detail.advanceRateTestStatus || '--',
    };
  }), [details, rows]);

  const facilityColumns = useMemo<ColDef<any>[]>(() => [
    {
      field: 'facilityName',
      headerName: 'Facility',
      minWidth: 320,
      pinned: 'left',
      getQuickFilterText: params => [params.data?.facilityName, params.data?.obligorName, params.data?.facilityCode].filter(Boolean).join(' '),
      cellRenderer: (params: any) => (
        <FacilityCell title={`${params.data.facilityName}\n${params.data.obligorName} · ${params.data.facilityCode}`}>
          <FacilityName>{params.data.facilityName}</FacilityName>
          <FacilityDetails>
            {params.data.obligorName !== '--' && <>{params.data.obligorName} · </>}
            <span className="code">{params.data.facilityCode}</span>
          </FacilityDetails>
        </FacilityCell>
      ),
    },
    { field: 'obligorName', headerName: 'Borrower', hide: true, minWidth: 230, cellStyle: centeredCell },
    { field: 'collateralType', headerName: 'Collateral', minWidth: 190, cellStyle: centeredCell },
    {
      field: 'drawnAmount',
      headerName: 'Drawn',
      minWidth: 125,
      type: 'rightAligned',
      valueFormatter: params => moneyFull(params.value, params.data?.currency),
      cellStyle: rightCell,
    },
    {
      field: 'borrowingBase',
      headerName: 'Borrowing Base',
      minWidth: 155,
      type: 'rightAligned',
      valueFormatter: params => moneyFull(params.value, params.data?.currency),
      cellStyle: rightCell,
    },
    {
      field: 'excessAvailability',
      headerName: 'Availability',
      minWidth: 140,
      type: 'rightAligned',
      valueFormatter: params => moneyFull(params.value, params.data?.currency),
      cellStyle: rightCell,
    },
    {
      headerName: 'Pool Health',
      minWidth: 210,
      valueGetter: params => params.data?.delinquencyPct,
      cellStyle: centeredCell,
      cellRenderer: (params: any) => (
        <FacilityCell>
          <RowTitle>{percent(params.data.delinquencyPct, 2)} delinquency</RowTitle>
          <RowSubtext>{percent(params.data.netLossRatePct, 2)} net loss · {numberCompact(params.data.numCollateralAssets)} assets</RowSubtext>
        </FacilityCell>
      ),
    },
    {
      field: 'advanceRateTestStatus',
      headerName: 'Test',
      maxWidth: 130,
      cellStyle: centeredCell,
      cellRenderer: (params: any) => (
        <StatusPill $tone={params.value === 'PASS' ? 'emerald' : 'red'}>
          {params.value || '--'}
        </StatusPill>
      ),
    },
  ], []);

  const tilesAndGrid = (
    <>
      <MetricStrip style={embedded ? { marginTop: 0 } : undefined}>
        <MetricCard $tone="cyan">
          <MetricLabel>Commitment</MetricLabel>
          <MetricValue>{moneyCompact(totalCommitment)}</MetricValue>
          <MetricSubtext>{numberCompact(rows.length)} facilities</MetricSubtext>
        </MetricCard>
        <MetricCard $tone="blue">
          <MetricLabel>Drawn</MetricLabel>
          <MetricValue>{moneyCompact(totalDrawn)}</MetricValue>
          <MetricSubtext>{percent(avgUtilization, 1)} commitment utilization</MetricSubtext>
        </MetricCard>
        <MetricCard $tone="violet">
          <MetricLabel>Borrowing Base</MetricLabel>
          <MetricValue>{moneyCompact(totalBorrowingBase)}</MetricValue>
          <MetricSubtext>Eligible collateral capacity</MetricSubtext>
        </MetricCard>
        <MetricCard $tone="emerald">
          <MetricLabel>Availability</MetricLabel>
          <MetricValue>{moneyCompact(totalAvailability)}</MetricValue>
          <MetricSubtext>{passCount} pass / {failCount} exception</MetricSubtext>
        </MetricCard>
      </MetricStrip>

      {error && <div style={{ color: '#fca5a5', margin: '1rem 0' }}>{error}</div>}
      {loading ? <EmptyStateBox>Loading ABF facilities...</EmptyStateBox> : rows.length === 0 ? (
        <EmptyStateBox>No ABF facilities yet. Onboard a facility to see it here.</EmptyStateBox>
      ) : (
        <div>
            <InstitutionalDataGrid
              title="ABF facility worklist"
              subtitle="Every facility with its drawn amount, borrowing base, availability, pool health and test status."
              rowData={facilityRows}
              columnDefs={facilityColumns}
              gridId="credit-abf-facility-worklist"
              autoHeight
              rowHeight={66}
              headerHeight={46}
              sizeColumnsToFit={false}
              searchPlaceholder="Search facility, borrower, collateral..."
              initialQuickFilter={worklistQuery}
              key={`abf-worklist-${worklistQuery}`}
              emptyLabel="No ABF facilities"
              getRowId={row => String(row.facilityId)}
              onRowClicked={openFacilityWorkspace}
            />
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div>
        {tilesAndGrid}
        <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
      </div>
    );
  }

  return (
    <WorkspacePanel>
      <ForwardFlowCheckerBar />
      <PanelHeader>
        <div>
          <PanelTitle>Asset-Based Finance</PanelTitle>
          <PanelSubtitle>
            Borrowing-base facilities, funded IBOR positions, pool surveillance and lifecycle posting.
          </PanelSubtitle>
        </div>
        {/* FR-1.1: one entry, both lanes; the acquisition ticket is a later step,
            not the way a facility is onboarded. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* One ABF home: facilities, forward-flow and the board/queue all live behind the
              segmented control there, so the two products are cleanly separated. */}
          <WorkspaceButton onClick={() => navigate(ABF_HOME_PATH)}>Open ABF workspace →</WorkspaceButton>
          <WorkspaceButton $variant="primary" onClick={() => setChoosingLane(true)}>
            Onboard a facility
          </WorkspaceButton>
        </div>
      </PanelHeader>
      <PanelBody>
        {choosingLane && <NewFacilityLanes onClose={() => setChoosingLane(false)} />}
        <FacilityOnboardingQueue />
        {tilesAndGrid}
      </PanelBody>

      <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
    </WorkspacePanel>
  );
};

export default ABFManagementScreen;
